/**
 * Payment Service Contracts
 *
 * Zod schemas for Payment Service (cascade payments) inter-service communication.
 * Backend ↔ Payment Service (Port 45790)
 *
 * Source of truth:
 * - services/payment-service/src/routes/cascade.ts (endpoint definitions)
 * - backend/src/services/payment-service.ts (PaymentServiceClient wrapper)
 *
 * Provider Response Field Support:
 * | Field          | FastPay | Collybus | NirvanaPay | EasySend/1Pays |
 * |----------------|---------|----------|------------|----------------|
 * | amount         | ✓       | ✓        | ✓          | ✓              |
 * | usdtAmount     | ✓       | ✗        | ✓          | ✗              |
 * | exchangeRate   | ✓       | ✗        | ✓          | ✗              |
 * | commission     | ✗       | ✗        | ✓          | ✗              |
 * | cardNumber     | ✓       | ✓        | ✓          | ✓              |
 * | cardHolderName | ✓       | ✓        | ✓          | ✓              |
 * | bank           | ✓       | ✓        | ✓          | ✓              |
 * | paymentUrl     | ✓       | ✓        | ✗          | ✓              |
 */
import { z } from 'zod';
// =============================================================================
// PAYMENT STATUS (shared across all operations)
// =============================================================================
/**
 * Normalized payment status values.
 * All providers map their raw statuses to these standard values.
 */
export const paymentStatusSchema = z.enum([
    'pending', // Waiting for user action
    'processing', // Provider is processing
    'completed', // Payment confirmed
    'failed', // Payment failed
    'cancelled', // Payment cancelled/expired
    'not_found', // Transaction not found
]);
// =============================================================================
// FORMATTED DEPOSIT RESPONSE
// Standard format that payment-service outputs to backend
// =============================================================================
/**
 * Formatted deposit response - standardized across all providers.
 * Maps directly to database columns. Optional fields are null when provider
 * doesn't support them.
 *
 * Used for both createDeposit and checkStatus responses.
 */
export const formattedDepositResponseSchema = z.object({
    // === Required fields (every provider returns these) ===
    providerMethod: z.string(), // e.g., 'fservice', 'cservice', 'nservice'
    providerTransactionId: z.string(), // Provider's deal/payment ID
    status: paymentStatusSchema, // Normalized status
    // === Financial data (optional - not all providers return these) ===
    amount: z.number().optional(), // Amount in fiat (AZN)
    usdtAmount: z.number().optional(), // Amount in USDT (FastPay, NirvanaPay)
    exchangeRate: z.number().optional(), // AZN/USDT rate (FastPay, NirvanaPay)
    commission: z.number().optional(), // Commission charged (NirvanaPay only)
    // === H2H mode - card details for user to transfer to ===
    cardNumber: z.string().optional(), // Card number
    cardHolderName: z.string().optional(), // Recipient name
    bank: z.string().optional(), // Bank name
    // === Redirect mode ===
    paymentUrl: z.string().optional(), // URL for redirect flow
});
/**
 * Full deposit method response - wrapper with success flag and raw data.
 * This is what payment-service returns to backend.
 */
export const depositMethodResponseSchema = z.object({
    success: z.boolean(),
    error: z.string().optional(),
    // Formatted for backend (maps to DB columns)
    formatted: formattedDepositResponseSchema.optional(),
    // Raw API response for logging/debugging (stored in deposit_logs.api_response)
    raw: z.record(z.unknown()).optional(),
});
// =============================================================================
// FORMATTED PAYOUT RESPONSE
// Standard format for withdrawal/payout operations
// =============================================================================
/**
 * Formatted payout response - standardized across all providers.
 */
export const formattedPayoutResponseSchema = z.object({
    // === Required fields ===
    providerMethod: z.string(),
    providerTransactionId: z.string(),
    status: paymentStatusSchema,
    // === Financial data ===
    amount: z.number().optional(),
    usdtAmount: z.number().optional(),
    exchangeRate: z.number().optional(),
    commission: z.number().optional(),
    // === Destination ===
    cardNumber: z.string().optional(), // User's card (where funds sent)
});
/**
 * Full payout method response.
 */
export const payoutMethodResponseSchema = z.object({
    success: z.boolean(),
    error: z.string().optional(),
    formatted: formattedPayoutResponseSchema.optional(),
    raw: z.record(z.unknown()).optional(),
});
// =============================================================================
// DEPOSIT CASCADE
// POST /api/cascade/deposit/start
// =============================================================================
export const depositCascadeStartRequestSchema = z.object({
    telegramId: z.number(), // User's Telegram ID
    amount: z.number().positive(), // Deposit amount
});
/**
 * Cascade attempt record - logged for each provider tried
 */
export const cascadeAttemptSchema = z.object({
    provider: z.string(),
    result: z.enum(['success', 'failed', 'skipped']).optional(),
    error: z.string().optional(),
    durationMs: z.number().optional(),
});
/**
 * Deposit cascade result - returned from payment service
 */
export const depositCascadeResultSchema = z.object({
    success: z.boolean(),
    dealId: z.string().optional(), // Provider's deal ID
    method: z.string().optional(), // Payment method that succeeded
    cardNumber: z.string().optional(), // Card number to pay to (from provider)
    cardHolderName: z.string().optional(),
    bank: z.string().optional(),
    paymentUrl: z.string().optional(), // Direct payment URL (some providers)
    providerData: z.record(z.unknown()).optional(), // Raw provider response
    error: z.string().optional(),
    attempts: z.array(cascadeAttemptSchema).optional(), // All attempts made
});
// =============================================================================
// DEPOSIT STATUS CHECK
// POST /api/cascade/deposit/check
// =============================================================================
export const depositStatusCheckRequestSchema = z.object({
    method: z.string(), // Payment method (e.g., 'fservice')
    dealId: z.string(), // Provider's deal ID
});
/**
 * Deposit status check result - uses the standard DepositMethodResponse.
 * The response contains: { success, formatted?, raw?, error? }
 *
 * Backend should read: response.formatted?.status for normalized status
 */
export const depositStatusCheckResultSchema = depositMethodResponseSchema;
// Legacy alias for backward compatibility
export const cascadeStatusSchema = paymentStatusSchema;
// =============================================================================
// PAYOUT CASCADE
// POST /api/cascade/payout/start
// =============================================================================
export const payoutCascadeStartRequestSchema = z.object({
    telegramId: z.number(),
    amount: z.number().positive(),
    cardNumber: z.string(), // 16-digit card number
    cardholderName: z.string().optional(),
    transactionId: z.string().optional(), // Withdrawal reference number for correlation
});
/**
 * Payout status values - uses the shared paymentStatusSchema
 */
export const payoutStatusSchema = paymentStatusSchema;
/**
 * Payout cascade result
 */
export const payoutCascadeResultSchema = z.object({
    success: z.boolean(),
    payoutId: z.string().optional(), // Provider's payout ID
    status: payoutStatusSchema.optional(),
    method: z.string().optional(), // Payment method that succeeded
    amount: z.number().optional(),
    cardNumber: z.string().optional(),
    providerData: z.record(z.unknown()).optional(),
    error: z.string().optional(),
    attempts: z.array(cascadeAttemptSchema).optional(),
});
// =============================================================================
// PAYOUT STATUS CHECK
// POST /api/cascade/payout/check
// =============================================================================
export const payoutStatusCheckRequestSchema = z.object({
    method: z.string(),
    payoutId: z.string(),
});
/**
 * Payout status check result - uses the standard PayoutMethodResponse.
 * Backend should read: response.formatted?.status for normalized status
 */
export const payoutStatusCheckResultSchema = payoutMethodResponseSchema;
// =============================================================================
// AVAILABLE METHODS
// GET /api/cascade/deposit/methods?amount=X
// GET /api/cascade/payout/methods?amount=X
// =============================================================================
export const availableMethodsResponseSchema = z.object({
    methods: z.array(z.string()), // List of available method names
});
// =============================================================================
// HEALTH CHECK
// GET /health
// =============================================================================
export const paymentHealthCheckResponseSchema = z.object({
    status: z.literal('ok'),
    service: z.literal('payment-service'),
    timestamp: z.string(),
});
//# sourceMappingURL=payment.js.map